import React from 'react';
import { useSearchParams } from 'react-router-dom';
import ActionButton from '../components/ActionButton';

const ServicesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeService =
    tabParam === 'rental' || tabParam === 'catering' || tabParam === 'shop'
      ? tabParam
      : 'shop';

  const handleServiceTabChange = (service) => {
    if (service === 'shop') {
      setSearchParams({});
      return;
    }

    setSearchParams({ tab: service });
  };

  const cateringHighlights = [
    {
      icon: '👨‍🍳',
      title: 'Expert chefs',
      description: 'Professional kitchen team experienced in plated service, buffets, and interactive stations.'
    },
    {
      icon: '🎯',
      title: 'Custom menus',
      description: 'Work with our team to design a menu tailored to your guests, dietary needs, and budget.'
    },
    {
      icon: '🏆',
      title: 'End-to-end service',
      description: 'Setup, service, clearing, and cleanup included. You focus on celebrating.'
    },
    {
      icon: '📦',
      title: 'Complete logistics',
      description: 'Equipment, linens, tableware, and serving tools, we bring it all.'
    }
  ];

  const rentalHighlights = [
    {
      icon: '🔥',
      title: 'Restaurant-grade equipment',
      description: 'Professional-quality cookware, burners, and prep stations used by Asaba chefs.'
    },
    {
      icon: '✨',
      title: 'Sanitized & maintained',
      description: 'Every item cleaned and inspected between rentals. Ready to use immediately.'
    },
    {
      icon: '🚚',
      title: 'Delivery & pickup',
      description: 'Same-day delivery across Asaba, Delta State. Return logistics handled seamlessly.'
    },
    {
      icon: '📋',
      title: 'Transparent pricing',
      description: 'Clear rental terms, deposit policies, and no hidden fees.'
    }
  ];

  const shopHighlights = [
    {
      icon: '🕐',
      title: 'Open daily',
      description: 'Monday–Saturday 10 AM–8 PM, Sunday 12 PM–6 PM. Consistent hours, fresh daily prep.'
    },
    {
      icon: '🎫',
      title: 'Walk-in & orders',
      description: 'Order on the spot or call ahead. Pickup or delivery from  FMC to anywhere in asaba.'
    },
    {
      icon: '🪑',
      title: 'Dine-in seating',
      description: 'Limited indoor and outdoor seating for quick meals, coffee breaks, or casual gatherings.'
    },
    {
      icon: '🎨',
      title: 'Customizable portions',
      description: 'Single plates, family packs, or bulk catering orders all available same-day.'
    }
  ];

  const cateringProcessSteps = [
    { step: '1', title: 'Inquiry', description: 'Contact us with event details: date, location, guest count, dietary needs.' },
    { step: '2', title: 'Proposal', description: 'We prepare a menu proposal, quote, and booking timeline.' },
    { step: '3', title: 'Tasting', description: 'Optional: preview plated dishes or sample menu items.' },
    { step: '4', title: 'Confirmation', description: 'Finalize menu, seating, service style, and logistics.' },
    { step: '5', title: 'Execution', description: 'Our crew arrives early, preps, serves, and cleans up post-event.' }
  ];

  const rentalProcessSteps = [
    { step: '1', title: 'Browse', description: 'Browse rental inventory by category on our Equipment Rentals page.' },
    { step: '2', title: 'Select', description: 'Choose items and quantities. Check availability and pricing.' },
    { step: '3', title: 'Book', description: 'Confirm rental dates, delivery address, and deposit.' },
    { step: '4', title: 'Delivery', description: 'Equipment delivered sanitized and ready. Rules and care guide included.' },
    { step: '5', title: 'Return', description: 'Return items to venue or arrange pickup. Final reconciliation and release of deposit.' }
  ];

  const isRental = activeService === 'rental';
  const isCatering = activeService === 'catering';
  const isShop = activeService === 'shop';

  return (
    <main className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-content">
          <p className="eyebrow">Our services</p>
          <h1>Meals, catering, and rentals with one kitchen standard.</h1>
          <p>
            Choose the part of Vemmac Kitchen you need today: fresh shop meals,
            catered event support, or clean equipment rentals for your own setup.
          </p>
        </div>
      </section>

      {/* Services Navigation Tabs */}
      <section className="services-tabs-section">
        <div className="services-tabs">
          <button
            className={`service-tab ${isRental ? 'active' : ''}`}
            onClick={() => handleServiceTabChange('rental')}
            type="button"
          >
            Rentals
          </button>
          <button
            className={`service-tab ${isCatering ? 'active' : ''}`}
            onClick={() => handleServiceTabChange('catering')}
            type="button"
          >
            Catering
          </button>
          <button
            className={`service-tab ${isShop ? 'active' : ''}`}
            onClick={() => handleServiceTabChange('shop')}
            type="button"
          >
            Shop
          </button>
        </div>
        <div className="services-tab-link-actions">
          {isRental && (
            <ActionButton to="/services/rental" variant="outline" size="xs">
              Open rental details page
            </ActionButton>
          )}
          {isCatering && (
            <ActionButton to="/services/catering" variant="outline" size="xs">
              Open catering details page
            </ActionButton>
          )}
        </div>
      </section>

      {isRental && (
        <section className="service-detail-section rental-detail">
          <div className="service-header">
            <div className="service-header-content">
              <h2>Equipment Rentals</h2>
              <p>
                Rent restaurant-grade cooking and serving equipment for home events, outdoor parties,
                and professional catering operations across Asaba.
              </p>
            </div>
            <div className="service-header-image">
              <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=900&q=80" alt="Equipment rentals" />
            </div>
          </div>

          <div className="service-highlights">
            <h3>Why rent with us</h3>
            <div className="highlights-grid">
              {rentalHighlights.map((item, index) => (
                <div key={index} className="highlight-card">
                  <div className="highlight-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="service-process">
            <h3>How rentals work</h3>
            <div className="process-steps">
              {rentalProcessSteps.map((item) => (
                <div key={item.step} className="process-step">
                  <div className="step-number">{item.step}</div>
                  <div className="step-content">
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          
        </section>
      )}

      {isCatering && (
        <section className="service-detail-section catering-detail">
          <div className="service-header">
            <div className="service-header-content">
              <h2>Catering Services</h2>
              <p>
                From intimate dinners to large celebrations, our catering team handles menu planning,
                preparation, serving, and post-event cleanup.
              </p>
            </div>
            <div className="service-header-image">
              <img src="https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?auto=format&fit=crop&w=900&q=80" alt="Catering service" />
            </div>
          </div>

          <div className="service-highlights">
            <h3>What you get</h3>
            <div className="highlights-grid">
              {cateringHighlights.map((item, index) => (
                <div key={index} className="highlight-card">
                  <div className="highlight-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>

         

          <div className="service-process">
            <h3>How catering works</h3>
            <div className="process-steps">
              {cateringProcessSteps.map((item) => (
                <div key={item.step} className="process-step">
                  <div className="step-number">{item.step}</div>
                  <div className="step-content">
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </section>
      )}

      {isShop && (
        <section className="service-detail-section shop-detail">
          <div className="service-header">
            <div className="service-header-content">
              <h2>Flagship Shop</h2>
              <p>
                Our Asaba location offers fresh, made-to-order meals daily. Walk in for quick bites,
                order ahead for family packs, or arrange catering for larger quantities.
              </p>
            </div>
            <div className="service-header-image">
              <img src="https://images.unsplash.com/photo-1554118811-1e0d58224e24?auto=format&fit=crop&w=600&q=80" alt="Flagship shop" />
            </div>
          </div>

          <div className="service-highlights">
            <h3>Shop experience</h3>
            <div className="highlights-grid">
              {shopHighlights.map((item, index) => (
                <div key={index} className="highlight-card">
                  <div className="highlight-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="shop-info">
            <div className="shop-hours">
              <h3>Operating hours</h3>
              <div className="hours-list">
                <div className="hour-item">
                  <strong>Monday–Saturday:</strong>
                  <span>10 AM – 8 PM</span>
                </div>
                <div className="hour-item">
                  <strong>Sunday:</strong>
                  <span>12 PM – 6 PM</span>
                </div>
                <div className="hour-note">
                  Closed on public holidays. Call ahead for bulk orders.
                </div>
              </div>
            </div>

            <div className="shop-location">
              <h3>Location</h3>
              <div className="location-info">
                <p><strong>Vemmac Kitchen</strong></p>
                <p>Fmc Line Shop 1<br />Asaba, Delta State <br />Nigeria</p>
                <p><strong>Phone:</strong> +234 806 051 2820</p>
                <p><strong>Email:</strong> vemmackitchenasaba@gmail.com</p>
              </div>
            </div>
          </div>

          <div className="shop-offerings">
            <h3>What's available</h3>
            <div className="offerings-grid">
              <div className="offering-card">
                <div className="offering-icon">🍽️</div>
                <h4>Fresh meals</h4>
                <p>Jollof rice, fried rice, soups, and sides cooked fresh daily</p>
              </div>
              <div className="offering-card">
                <div className="offering-icon">🥘</div>
                <h4>Combos</h4>
                <p>Pre-set meal combinations with mains, proteins, and sides</p>
              </div>
              <div className="offering-card">
                <div className="offering-icon">📦</div>
                <h4>Family packs</h4>
                <p>Large quantities perfect for family dinners and small gatherings</p>
              </div>
              <div className="offering-card">
                <div className="offering-icon">🚚</div>
                <h4>Delivery</h4>
                <p>Order online or call for same-day delivery across Asaba, Delta State</p>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h3>Visit or order from our shop</h3>
            <p>Stop by for a meal, call ahead for a bulk order, or browse our menu online.</p>
            <ActionButton to="/menu" variant="primary" size="sm">
              View our menu
            </ActionButton>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="services-final-cta">
        <h2>Ready to work with us?</h2>
        <p>Whether it's a catered event, equipment rental, or a quick meal, we're here to help.</p>
        <div className="final-cta-buttons">
          <ActionButton to="/contact" variant="primary" size="sm">
            Get in touch
          </ActionButton>
          {isRental && (
            <ActionButton to="/services/rental" variant="outline" size="sm">
              Browse rentals
            </ActionButton>
          )}
          {isCatering && (
            <ActionButton to="/services/catering" variant="outline" size="sm">
              Browse catering
            </ActionButton>
          )}
          {isShop && (
            <ActionButton to="/menu" variant="outline" size="sm">
              Browse menu
            </ActionButton>
          )}
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
